import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  IconButton,
  Spinner,
  Badge,
  Button,
  Divider,
  useToast,
} from "@chakra-ui/react";
import { AiOutlinePlayCircle, AiOutlinePauseCircle } from "react-icons/ai";
import { ArrowBackIcon } from "@chakra-ui/icons";
import WaveSurfer from "wavesurfer.js";
import PageLayoutWithHeaderNav from "./PageLayoutWithHeaderNav";
import {
  fetchAudioSnippet,
  getAudioSnippetUrl,
} from "../services/audioSnippetService";
import { formatDate } from "../utils/dateUtils";

const AudioSnippetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Unfiltered audio state
  const [unfilteredUrl, setUnfilteredUrl] = useState(null);
  const [isPlayingUnfiltered, setIsPlayingUnfiltered] = useState(false);
  const [loadingUnfiltered, setLoadingUnfiltered] = useState(false);
  const waveformUnfilteredRef = useRef(null);
  const waveSurferUnfilteredRef = useRef(null);

  // Filtered audio state
  const [filteredUrl, setFilteredUrl] = useState(null);
  const [isPlayingFiltered, setIsPlayingFiltered] = useState(false);
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const waveformFilteredRef = useRef(null);
  const waveSurferFilteredRef = useRef(null);

  // Refs for cleanup
  const isMountedRef = useRef(true);
  const timeoutIdsRef = useRef([]);

  // Load snippet details
  useEffect(() => {
    const abortController = new AbortController();

    const loadSnippet = async () => {
      try {
        setLoading(true);
        const data = await fetchAudioSnippet(id, abortController.signal);
        if (data && isMountedRef.current) {
          setSnippet(data);
        }
      } catch (err) {
        if (err && isMountedRef.current) {
          console.error("Error loading audio snippet:", err);
          setError("Failed to load audio snippet");
          toast({
            title: "Error",
            description: "Failed to load audio snippet details",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    if (id) {
      loadSnippet();
    }

    return () => {
      abortController.abort();
    };
  }, [id, toast]);

  // Initialize WaveSurfer for unfiltered audio
  const initializeWaveSurferUnfiltered = useCallback(() => {
    if (waveformUnfilteredRef.current && !waveSurferUnfilteredRef.current) {
      waveSurferUnfilteredRef.current = WaveSurfer.create({
        container: waveformUnfilteredRef.current,
        waveColor: "rgb(59, 130, 246)", // blue-500
        progressColor: "rgb(37, 99, 235)", // blue-600
        cursorColor: "rgb(29, 78, 216)", // blue-700
        barWidth: 2,
        barRadius: 1,
        responsive: true,
        height: 100,
        normalize: true,
        backend: "WebAudio",
      });

      waveSurferUnfilteredRef.current.on("play", () => {
        setIsPlayingUnfiltered(true);
      });

      waveSurferUnfilteredRef.current.on("pause", () => {
        setIsPlayingUnfiltered(false);
      });

      waveSurferUnfilteredRef.current.on("finish", () => {
        setIsPlayingUnfiltered(false);
      });

      waveSurferUnfilteredRef.current.on("error", (error) => {
        console.error("WaveSurfer unfiltered error:", error);
      });
    }
  }, []);

  // Initialize WaveSurfer for filtered audio
  const initializeWaveSurferFiltered = useCallback(() => {
    if (waveformFilteredRef.current && !waveSurferFilteredRef.current) {
      waveSurferFilteredRef.current = WaveSurfer.create({
        container: waveformFilteredRef.current,
        waveColor: "rgb(16, 185, 129)", // green-500
        progressColor: "rgb(5, 150, 105)", // green-600
        cursorColor: "rgb(4, 120, 87)", // green-700
        barWidth: 2,
        barRadius: 1,
        responsive: true,
        height: 100,
        normalize: true,
        backend: "WebAudio",
      });

      waveSurferFilteredRef.current.on("play", () => {
        setIsPlayingFiltered(true);
      });

      waveSurferFilteredRef.current.on("pause", () => {
        setIsPlayingFiltered(false);
      });

      waveSurferFilteredRef.current.on("finish", () => {
        setIsPlayingFiltered(false);
      });

      waveSurferFilteredRef.current.on("error", (error) => {
        console.error("WaveSurfer filtered error:", error);
      });
    }
  }, []);

  // Load unfiltered audio
  const loadUnfilteredAudio = useCallback(async () => {
    if (!snippet || !snippet.unfiltered_audio_url || !isMountedRef.current) return;

    const abortController = new AbortController();
    
    try {
      if (isMountedRef.current) {
        setLoadingUnfiltered(true);
      }
      const url = await getAudioSnippetUrl(id, "unfiltered", abortController.signal);
      
      if (url && isMountedRef.current) {
        setUnfilteredUrl(url);
        
        // Initialize and load audio
        const timeout1 = setTimeout(() => {
          if (isMountedRef.current) {
            initializeWaveSurferUnfiltered();
            const timeout2 = setTimeout(() => {
              if (isMountedRef.current && waveSurferUnfilteredRef.current) {
                try {
                  waveSurferUnfilteredRef.current.load(url);
                } catch (error) {
                  // Silently ignore AbortError from WaveSurfer load
                  if (error.name !== 'AbortError') {
                    console.error("Error loading unfiltered audio:", error);
                  }
                }
              }
            }, 100);
            timeoutIdsRef.current.push(timeout2);
          }
        }, 100);
        timeoutIdsRef.current.push(timeout1);
      }
    } catch (err) {
      if (isMountedRef.current && err && err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error("Error loading unfiltered audio:", err);
        toast({
          title: "Error",
          description: "Failed to load unfiltered audio",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingUnfiltered(false);
      }
    }

    return () => {
      abortController.abort();
    };
  }, [snippet, id, initializeWaveSurferUnfiltered, toast]);

  // Load filtered audio
  const loadFilteredAudio = useCallback(async () => {
    if (!snippet || !snippet.filtered_audio_url || !isMountedRef.current) return;

    const abortController = new AbortController();
    
    try {
      if (isMountedRef.current) {
        setLoadingFiltered(true);
      }
      const url = await getAudioSnippetUrl(id, "filtered", abortController.signal);
      
      if (url && isMountedRef.current) {
        setFilteredUrl(url);
        
        // Initialize and load audio
        const timeout1 = setTimeout(() => {
          if (isMountedRef.current) {
            initializeWaveSurferFiltered();
            const timeout2 = setTimeout(() => {
              if (isMountedRef.current && waveSurferFilteredRef.current) {
                try {
                  waveSurferFilteredRef.current.load(url);
                } catch (error) {
                  // Silently ignore AbortError from WaveSurfer load
                  if (error.name !== 'AbortError') {
                    console.error("Error loading filtered audio:", error);
                  }
                }
              }
            }, 100);
            timeoutIdsRef.current.push(timeout2);
          }
        }, 100);
        timeoutIdsRef.current.push(timeout1);
      }
    } catch (err) {
      if (isMountedRef.current && err && err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error("Error loading filtered audio:", err);
        toast({
          title: "Error",
          description: "Failed to load filtered audio",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingFiltered(false);
      }
    }

    return () => {
      abortController.abort();
    };
  }, [snippet, id, initializeWaveSurferFiltered, toast]);

  // Load audio when snippet is available
  useEffect(() => {
    if (snippet) {
      if (snippet.unfiltered_audio_url) {
        loadUnfilteredAudio();
      }
      if (snippet.filtered_audio_url) {
        loadFilteredAudio();
      }
    }
  }, [snippet, loadUnfilteredAudio, loadFilteredAudio]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      // Clear all timeouts
      timeoutIdsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeoutIdsRef.current = [];

      // Destroy WaveSurfer instances
      try {
        if (waveSurferUnfilteredRef.current) {
          waveSurferUnfilteredRef.current.destroy();
          waveSurferUnfilteredRef.current = null;
        }
      } catch (error) {
        // Silently ignore all WaveSurfer destroy errors (including AbortError)
        if (error.name !== 'AbortError') {
          console.debug("WaveSurfer unfiltered cleanup:", error.message);
        }
      }

      try {
        if (waveSurferFilteredRef.current) {
          waveSurferFilteredRef.current.destroy();
          waveSurferFilteredRef.current = null;
        }
      } catch (error) {
        // Silently ignore all WaveSurfer destroy errors (including AbortError)
        if (error.name !== 'AbortError') {
          console.debug("WaveSurfer filtered cleanup:", error.message);
        }
      }
    };
  }, []);

  const handlePlayPauseUnfiltered = () => {
    if (waveSurferUnfilteredRef.current) {
      waveSurferUnfilteredRef.current.playPause();
    }
  };

  const handlePlayPauseFiltered = () => {
    if (waveSurferFilteredRef.current) {
      waveSurferFilteredRef.current.playPause();
    }
  };

  const formatDuration = (startTimestamp, endTimestamp) => {
    if (!startTimestamp || !endTimestamp) return "N/A";
    const start = new Date(startTimestamp);
    const end = new Date(endTimestamp);
    const durationSeconds = (end - start) / 1000;
    
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = Math.floor(durationSeconds % 60);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <PageLayoutWithHeaderNav>
        <VStack spacing={6} align="stretch">
          <HStack>
            <IconButton
              icon={<ArrowBackIcon />}
              onClick={() => navigate("/audio-snippets")}
              variant="ghost"
              aria-label="Back to audio snippets"
            />
            <Heading size="lg" color="gray.700">
              Audio Snippet Details
            </Heading>
          </HStack>

          <Card>
            <CardBody>
              <VStack spacing={4} align="center" py={12}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
                <Text fontSize="lg" fontWeight="medium" color="gray.700">
                  Loading audio snippet...
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </PageLayoutWithHeaderNav>
    );
  }

  if (error || !snippet) {
    return (
      <PageLayoutWithHeaderNav>
        <VStack spacing={6} align="stretch">
          <HStack>
            <IconButton
              icon={<ArrowBackIcon />}
              onClick={() => navigate("/audio-snippets")}
              variant="ghost"
              aria-label="Back to audio snippets"
            />
            <Heading size="lg" color="gray.700">
              Audio Snippet Details
            </Heading>
          </HStack>

          <Card>
            <CardBody>
              <VStack spacing={4} align="center" py={12}>
                <Text color="red.500" fontSize="lg">
                  {error || "Audio snippet not found"}
                </Text>
                <Button onClick={() => navigate("/audio-snippets")}>
                  Back to Audio Snippets
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </PageLayoutWithHeaderNav>
    );
  }

  return (
    <PageLayoutWithHeaderNav>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack>
            <IconButton
              icon={<ArrowBackIcon />}
              onClick={() => navigate("/audio-snippets")}
              variant="ghost"
              aria-label="Back to audio snippets"
            />
            <Heading size="lg" color="gray.700">
              Audio Snippet #{snippet.id}
            </Heading>
          </HStack>
          <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
            {snippet.filtered_audio_url && snippet.unfiltered_audio_url
              ? "Both Available"
              : snippet.unfiltered_audio_url
              ? "Unfiltered Only"
              : "Filtered Only"}
          </Badge>
        </HStack>

        {/* Metadata Card */}
        <Card>
          <CardHeader>
            <Heading size="md">Details</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between">
                <Text fontWeight="semibold">Start Time:</Text>
                <Text fontSize="sm" color="gray.600">
                  {formatDate(snippet.start_timestamp)}
                </Text>
              </HStack>
              <Divider />
              <HStack justify="space-between">
                <Text fontWeight="semibold">End Time:</Text>
                <Text fontSize="sm" color="gray.600">
                  {formatDate(snippet.end_timestamp)}
                </Text>
              </HStack>
              <Divider />
              <HStack justify="space-between">
                <Text fontWeight="semibold">Duration:</Text>
                <Text fontSize="sm" color="gray.600">
                  {formatDuration(snippet.start_timestamp, snippet.end_timestamp)}
                </Text>
              </HStack>
              <Divider />
              <HStack justify="space-between">
                <Text fontWeight="semibold">Created:</Text>
                <Text fontSize="sm" color="gray.600">
                  {formatDate(snippet.created_at)}
                </Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Unfiltered Audio Card */}
        {snippet.unfiltered_audio_url && (
          <Card>
            <CardHeader>
              <HStack justify="space-between" align="center">
                <Heading size="md">Unfiltered Audio</Heading>
                <Badge colorScheme="blue">Original</Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              {loadingUnfiltered ? (
                <VStack spacing={4} py={8}>
                  <Spinner color="blue.500" />
                  <Text color="gray.600">Loading unfiltered audio...</Text>
                </VStack>
              ) : unfilteredUrl ? (
                <VStack spacing={3} align="stretch">
                  {/* WaveSurfer visualization */}
                  <Box
                    ref={waveformUnfilteredRef}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="gray.50"
                    minHeight="120px"
                  />

                  {/* Playback controls */}
                  <HStack spacing={3}>
                    <IconButton
                      icon={
                        isPlayingUnfiltered ? (
                          <AiOutlinePauseCircle />
                        ) : (
                          <AiOutlinePlayCircle />
                        )
                      }
                      onClick={handlePlayPauseUnfiltered}
                      colorScheme="blue"
                      variant="solid"
                      size="md"
                      aria-label={
                        isPlayingUnfiltered ? "Pause audio" : "Play audio"
                      }
                    />
                    <Text fontSize="sm" color="gray.600">
                      {isPlayingUnfiltered
                        ? "Playing unfiltered audio..."
                        : "Click play to listen to unfiltered audio"}
                    </Text>
                  </HStack>
                </VStack>
              ) : (
                <Text color="gray.500" textAlign="center" py={8}>
                  Unfiltered audio not available
                </Text>
              )}
            </CardBody>
          </Card>
        )}

        {/* Filtered Audio Card */}
        {snippet.filtered_audio_url && (
          <Card>
            <CardHeader>
              <HStack justify="space-between" align="center">
                <Heading size="md">Filtered Audio</Heading>
                <Badge colorScheme="green">Processed</Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              {loadingFiltered ? (
                <VStack spacing={4} py={8}>
                  <Spinner color="green.500" />
                  <Text color="gray.600">Loading filtered audio...</Text>
                </VStack>
              ) : filteredUrl ? (
                <VStack spacing={3} align="stretch">
                  {/* WaveSurfer visualization */}
                  <Box
                    ref={waveformFilteredRef}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="gray.50"
                    minHeight="120px"
                  />

                  {/* Playback controls */}
                  <HStack spacing={3}>
                    <IconButton
                      icon={
                        isPlayingFiltered ? (
                          <AiOutlinePauseCircle />
                        ) : (
                          <AiOutlinePlayCircle />
                        )
                      }
                      onClick={handlePlayPauseFiltered}
                      colorScheme="green"
                      variant="solid"
                      size="md"
                      aria-label={
                        isPlayingFiltered ? "Pause audio" : "Play audio"
                      }
                    />
                    <Text fontSize="sm" color="gray.600">
                      {isPlayingFiltered
                        ? "Playing filtered audio..."
                        : "Click play to listen to filtered audio"}
                    </Text>
                  </HStack>
                </VStack>
              ) : (
                <Text color="gray.500" textAlign="center" py={8}>
                  Filtered audio not available
                </Text>
              )}
            </CardBody>
          </Card>
        )}

        {/* Transcription Card */}
        {(snippet.transcription_unfiltered || snippet.transcription_filtered) && (
          <Card>
            <CardHeader>
              <Heading size="md">Transcription</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                {/* Unfiltered Transcription */}
                {snippet.transcription_unfiltered && (
                  <Box>
                    <HStack mb={2}>
                      <Badge colorScheme="blue">Unfiltered</Badge>
                      <Text fontWeight="semibold" fontSize="sm">Original Audio Transcription</Text>
                    </HStack>
                    <Box
                      p={4}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {snippet.transcription_unfiltered}
                      </Text>
                    </Box>
                  </Box>
                )}

                {/* Filtered Transcription */}
                {snippet.transcription_filtered && (
                  <Box>
                    <HStack mb={2}>
                      <Badge colorScheme="green">Filtered</Badge>
                      <Text fontWeight="semibold" fontSize="sm">Processed Audio Transcription</Text>
                    </HStack>
                    <Box
                      p={4}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {snippet.transcription_filtered}
                      </Text>
                    </Box>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* No audio available message */}
        {!snippet.unfiltered_audio_url && !snippet.filtered_audio_url && (
          <Card>
            <CardBody>
              <Text color="gray.500" textAlign="center" py={8}>
                No audio files available for this snippet
              </Text>
            </CardBody>
          </Card>
        )}
      </VStack>
    </PageLayoutWithHeaderNav>
  );
};

export default AudioSnippetDetailPage;
